/**
 * useImageProtection - Previene descarga/screenshot de imágenes y detecta intentos
 *
 * - Bloquea clic derecho (guardar imagen)
 * - Bloquea arrastrar imágenes
 * - Desactiva menú contextual en móvil (long-press)
 * - Envía eventos a PostHog cuando el usuario intenta
 *
 * Usado en: página inicial (BaseTemplate), Portfolio
 */

import { useEffect } from "react";

const STYLE_ID = "image-protection-styles";

function trackProtectionAttempt(
  type: string,
  details?: Record<string, unknown>
) {
  if (typeof window === "undefined") return;
  const posthog = (window as unknown as { posthog?: { capture?: (e: string, p?: object) => void } }).posthog;
  if (posthog && typeof posthog.capture === "function") {
    posthog.capture("image_protection_attempt", {
      attempt_type: type,
      page_path: window.location.pathname,
      page_title: document.title,
      timestamp: new Date().toISOString(),
      ...details,
    });
  }
}

export function useImageProtection() {
  useEffect(() => {
    if (typeof document === "undefined") return;

    const handleContextMenu = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const isImage = target.tagName === "IMG" || target.closest("img");
      if (!isImage) return;
      e.preventDefault();
      e.stopPropagation();
      trackProtectionAttempt("right_click", { target: "img" });
      return false;
    };

    const handleDragStart = (e: DragEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === "IMG" || target.closest("img")) {
        e.preventDefault();
        trackProtectionAttempt("drag");
        return false;
      }
    };

    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length > 1) {
        trackProtectionAttempt("multi_touch", { touches: e.touches.length });
      }
    };

    let longPressTimer: ReturnType<typeof setTimeout> | null = null;
    const LONG_PRESS_MS = 500;

    const handleTouchStartLongPress = (e: TouchEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName !== "IMG" && !target.closest("img")) return;
      longPressTimer = setTimeout(() => {
        trackProtectionAttempt("long_press", { target: "img" });
      }, LONG_PRESS_MS);
    };

    const handleTouchEndLongPress = () => {
      if (longPressTimer) {
        clearTimeout(longPressTimer);
        longPressTimer = null;
      }
    };

    document.addEventListener("contextmenu", handleContextMenu);
    document.addEventListener("dragstart", handleDragStart, { capture: true });
    document.addEventListener("touchstart", handleTouchStart, { passive: true });
    document.addEventListener("touchstart", handleTouchStartLongPress, {
      passive: true,
    });
    document.addEventListener("touchend", handleTouchEndLongPress);
    document.addEventListener("touchcancel", handleTouchEndLongPress);

    if (!document.getElementById(STYLE_ID)) {
      const style = document.createElement("style");
      style.id = STYLE_ID;
      style.textContent = `
        img {
          -webkit-user-drag: none !important;
          -khtml-user-drag: none !important;
          -moz-user-drag: none !important;
          -o-user-drag: none !important;
          user-drag: none !important;
          -webkit-touch-callout: none !important;
          -webkit-user-select: none !important;
          user-select: none !important;
        }
      `;
      document.head.appendChild(style);
    }

    return () => {
      document.removeEventListener("contextmenu", handleContextMenu);
      document.removeEventListener("dragstart", handleDragStart, {
        capture: true,
      });
      document.removeEventListener("touchstart", handleTouchStart);
      document.removeEventListener("touchstart", handleTouchStartLongPress);
      document.removeEventListener("touchend", handleTouchEndLongPress);
      document.removeEventListener("touchcancel", handleTouchEndLongPress);
      if (longPressTimer) clearTimeout(longPressTimer);
    };
  }, []);
}
